import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../../utils/api';
import { toast } from 'react-toastify';
import { MessageSquare, ChevronDown, ChevronUp, Trash2, RotateCcw, ExternalLink } from 'lucide-react';

const PostsTab = ({ posts, postCategoryFilter, setPostCategoryFilter, moderatePost, fetchData }) => {
  const [view, setView] = useState('active'); // 'active' | 'trash'
  const [expandedComments, setExpandedComments] = useState(null);
  const [comments, setComments] = useState({});
  const [loadingComments, setLoadingComments] = useState(null);
  const [purging, setPurging] = useState(false);

  const handlePurgeTrash = async () => {
    if (!window.confirm(`Permanently delete all ${trashedPosts.length} trashed post(s)? This cannot be undone.`)) return;
    setPurging(true);
    try {
      const res = await api.delete('/admin/posts/purge-trash');
      toast.success(`Purged ${res.data?.purged || 0} post(s) from trash`);
      fetchData();
    } catch {
      toast.error('Failed to purge trash');
    } finally {
      setPurging(false);
    }
  };

  const activePosts = posts.filter(p => p.status !== 'deleted');
  const trashedPosts = posts.filter(p => p.status === 'deleted');

  const filtered = (view === 'trash' ? trashedPosts : activePosts)
    .filter(p => postCategoryFilter === 'all' || p.category === postCategoryFilter);

  const loadComments = async (postId) => {
    if (expandedComments === postId) {
      setExpandedComments(null);
      return;
    }
    setLoadingComments(postId);
    try {
      const res = await api.get(`/admin/posts/${postId}/comments`);
      setComments(prev => ({ ...prev, [postId]: res.data.comments || [] }));
      setExpandedComments(postId);
    } catch {
      toast.error('Failed to load comments');
    } finally {
      setLoadingComments(null);
    }
  };

  const deleteComment = async (postId, commentId) => {
    if (!window.confirm('Delete this comment?')) return;
    try {
      await api.delete(`/admin/posts/${postId}/comments/${commentId}`);
      setComments(prev => ({
        ...prev,
        [postId]: (prev[postId] || []).filter(c => c.id !== commentId)
      }));
      toast.success('Comment deleted');
    } catch {
      toast.error('Failed to delete comment');
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <h2 className="text-2xl font-bold text-white">Post Moderation</h2>
        <div className="flex gap-2 items-center flex-wrap">
          {/* View toggle */}
          <div className="flex gap-1 border border-gray-700 rounded overflow-hidden">
            <button
              onClick={() => setView('active')}
              className={`px-3 py-1.5 text-xs font-mono uppercase transition-colors ${view === 'active' ? 'bg-green-600 text-white' : 'bg-gray-800 text-gray-400 hover:bg-gray-700'}`}
            >
              Active ({activePosts.length})
            </button>
            <button
              onClick={() => setView('trash')}
              className={`flex items-center gap-1 px-3 py-1.5 text-xs font-mono uppercase transition-colors ${view === 'trash' ? 'bg-red-700 text-white' : 'bg-gray-800 text-gray-400 hover:bg-gray-700'}`}
            >
              <Trash2 className="w-3 h-3" /> Trash ({trashedPosts.length})
            </button>
          </div>
          {/* Purge all trash */}
          {view === 'trash' && trashedPosts.length > 0 && (
            <button
              onClick={handlePurgeTrash}
              disabled={purging}
              className="flex items-center gap-1.5 text-xs font-mono text-red-500 hover:text-red-400 border border-red-900 hover:border-red-700 px-3 py-1.5 rounded-sm transition-colors disabled:opacity-50"
              data-testid="purge-trash-btn"
            >
              <Trash2 className="w-3 h-3" /> {purging ? 'Purging...' : 'Purge All'}
            </button>
          )}
          {/* Category filters */}
          {['all', 'issue', 'positive', 'discussion'].map(cat => (
            <button
              key={cat}
              onClick={() => setPostCategoryFilter(cat)}
              className={`px-3 py-1 text-xs font-mono uppercase rounded transition-colors ${
                postCategoryFilter === cat ? 'bg-[#C5A52A] text-black' : 'bg-gray-800 text-gray-400 hover:bg-gray-700'
              }`}
            >
              {cat === 'all' ? 'All' : cat === 'issue' ? '⚠ Issues' : cat === 'positive' ? '✓ Kudos' : '💬 Discussion'}
            </button>
          ))}
        </div>
      </div>

      {filtered.length === 0 && (
        <p className="text-gray-500 text-sm font-mono text-center py-12">
          {view === 'trash' ? 'No trashed posts.' : 'No posts in this category.'}
        </p>
      )}

      <div className="space-y-4">
        {filtered.map((post) => (
          <div key={post.id} className="bg-gray-900 border border-gray-800 rounded-sm">
            <div className="p-5">
              <div className="flex justify-between items-start mb-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap mb-1">
                    <h3 className="text-white font-bold">{post.user_name}</h3>
                    {post.ward && <span className="text-[10px] font-mono text-blue-400 bg-blue-900/20 px-1.5 py-0.5 rounded">{post.ward}</span>}
                    {post.category === 'issue' && <span className="text-[10px] font-mono text-yellow-400 bg-yellow-900/20 px-1.5 py-0.5 rounded border border-yellow-800/40">⚠ ISSUE</span>}
                    {post.category === 'positive' && <span className="text-[10px] font-mono text-green-400 bg-green-900/20 px-1.5 py-0.5 rounded border border-green-800/40">✓ KUDOS</span>}
                    {/* Post ID — clickable link to post */}
                    <Link
                      to={`/post/${post.id}`}
                      target="_blank"
                      className="flex items-center gap-1 text-[10px] font-mono text-gray-600 hover:text-[#C5A52A] transition-colors"
                      title="Open post"
                    >
                      #{post.id?.slice(0, 8)} <ExternalLink className="w-2.5 h-2.5" />
                    </Link>
                  </div>
                  <p className="text-gray-500 text-xs font-mono">{new Date(post.created_at).toLocaleString()}</p>
                </div>
                <span className={`ml-4 flex-shrink-0 text-xs font-mono px-2 py-1 rounded ${
                  post.status === 'active' ? 'bg-green-900 text-green-500' :
                  post.status === 'moderated' ? 'bg-yellow-900 text-yellow-500' :
                  'bg-red-900 text-red-500'
                }`}>
                  {post.status}
                </span>
              </div>

              <p className="text-gray-300 mb-4 text-sm">{post.content}</p>

              {post.moderation_reason && (
                <p className="text-yellow-500 text-sm mb-3">Reason: {post.moderation_reason}</p>
              )}

              {/* Action buttons */}
              <div className="flex gap-2 flex-wrap items-center">
                {view === 'trash' ? (
                  /* Trash view: reinstate or permanent nuke */
                  <>
                    <button
                      onClick={() => { moderatePost(post.id, 'approve'); }}
                      className="flex items-center gap-1.5 bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-sm text-sm font-mono uppercase transition-colors"
                    >
                      <RotateCcw className="w-3.5 h-3.5" /> Reinstate
                    </button>
                  </>
                ) : (
                  /* Active view: normal moderation */
                  <>
                    {post.status !== 'active' && (
                      <button
                        onClick={() => moderatePost(post.id, 'approve')}
                        className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-sm text-sm font-mono uppercase transition-colors"
                      >
                        Approve
                      </button>
                    )}
                    {post.status === 'active' && (
                      <button
                        onClick={() => moderatePost(post.id, 'reject', 'Admin review')}
                        className="bg-yellow-600 hover:bg-yellow-700 text-black px-4 py-2 rounded-sm text-sm font-mono uppercase transition-colors"
                      >
                        Moderate
                      </button>
                    )}
                    {post.category === 'issue' && post.verification_status !== 'fixed' && (
                      <button
                        onClick={async () => {
                          try {
                            await api.put(`/admin/posts/${post.id}/moderate`, null, { params: { action: 'mark_fixed' } });
                            toast.success('Marked as fixed');
                            fetchData();
                          } catch {
                            try {
                              await api.post(`/posts/${post.id}/verify`, { verification_type: 'fixed', latitude: 0, longitude: 0, comment: 'Admin override' });
                              toast.success('Marked as fixed');
                              fetchData();
                            } catch { toast.error('Failed to mark as fixed'); }
                          }
                        }}
                        className="bg-green-900/50 hover:bg-green-900 text-green-400 border border-green-800 px-4 py-2 rounded-sm text-sm font-mono uppercase transition-colors"
                      >
                        Mark Fixed
                      </button>
                    )}
                    <button
                      onClick={() => moderatePost(post.id, 'delete')}
                      className="flex items-center gap-1.5 bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-sm text-sm font-mono uppercase transition-colors"
                    >
                      <Trash2 className="w-3.5 h-3.5" /> Delete
                    </button>
                  </>
                )}

                {/* Comments toggle */}
                <button
                  onClick={() => loadComments(post.id)}
                  className="flex items-center gap-1.5 ml-auto text-xs font-mono text-gray-500 hover:text-white border border-gray-700 hover:border-gray-500 px-3 py-2 rounded-sm transition-colors"
                  data-testid={`view-comments-${post.id}`}
                >
                  {loadingComments === post.id ? (
                    <span className="text-gray-500">Loading...</span>
                  ) : (
                    <>
                      <MessageSquare className="w-3.5 h-3.5" />
                      {post.comment_count || 0} Comments
                      {expandedComments === post.id ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
                    </>
                  )}
                </button>
              </div>
            </div>

            {/* Inline comments panel */}
            {expandedComments === post.id && (
              <div className="border-t border-gray-800 bg-gray-950 px-5 py-4">
                <p className="text-xs text-gray-500 uppercase tracking-wider font-mono mb-3">Comments</p>
                {(comments[post.id] || []).length === 0 ? (
                  <p className="text-gray-600 text-xs font-mono">No comments yet.</p>
                ) : (
                  <div className="space-y-2">
                    {(comments[post.id] || []).map(c => (
                      <div key={c.id} className="flex items-start justify-between gap-3 bg-gray-900 border border-gray-800 rounded px-3 py-2">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-0.5">
                            <span className="text-xs font-bold text-gray-300">{c.user_name || 'Unknown'}</span>
                            <span className="text-[10px] text-gray-600 font-mono">{new Date(c.created_at).toLocaleString()}</span>
                          </div>
                          <p className="text-sm text-gray-400">{c.content}</p>
                        </div>
                        <button
                          onClick={() => deleteComment(post.id, c.id)}
                          className="flex-shrink-0 text-red-600 hover:text-red-400 p-1 rounded transition-colors"
                          title="Delete comment"
                          data-testid={`delete-comment-${c.id}`}
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default PostsTab;
